import { useState, useCallback } from 'react';

/**
 * Принудително обновяване на приложението — дерегистрира service worker-ите,
 * изтрива кешовете и презарежда от сървъра.
 *
 * Досега тази логика живееше ВЪТРЕ в `AdminLayout`, затова бутонът го имаше
 * само админът. Учителят на телефон няма как да си изчисти кеша и остава на
 * стара версия, без да разбере — точно този случай докара дефекта, при който
 * поправка беше на production, а устройството показваше старото.
 *
 * Резултатът се записва в `sessionStorage` ПРЕДИ презареждането. Иначе се губи:
 * `location.reload()` унищожава цялото състояние, тоест операцията никога не
 * може да каже дали е успяла. Това е причината старият бутон да не връща нищо.
 */

const RESULT_KEY = 'app-refresh-result';

export interface RefreshResult {
  ok: boolean;
  /** Брой дерегистрирани service worker-и. */
  serviceWorkers: number;
  /** Брой изтрити кешове. */
  caches: number;
  /** Съобщение за грешка, когато `ok` е false. */
  error?: string;
}

/**
 * Прочита резултата от последното обновяване и го ИЗТРИВА.
 *
 * Изтриването е част от договора: ключът преживява презареждането, тоест без
 * него съобщението ще изскача при всяко следващо отваряне на приложението.
 */
export function consumeRefreshResult(): RefreshResult | null {
  try {
    const raw = sessionStorage.getItem(RESULT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(RESULT_KEY);
    return JSON.parse(raw) as RefreshResult;
  } catch {
    // Повреден запис или недостъпен sessionStorage (частен режим) — по-добре
    // без съобщение, отколкото счупен екран.
    return null;
  }
}

/**
 * Отбелязва изхода от PWA обновяването (бутона „Обнови" в синия банер).
 *
 * Този път е различен от `forceRefresh`: там ние чистим кеша, тук service
 * worker-ът се подменя сам. Общото е презареждането, което трие всичко —
 * затова и двата пътя оставят следа на едно и също място.
 */
export function markUpdateApplied(ok: boolean, error?: string): void {
  storeResult({ ok, serviceWorkers: ok ? 1 : 0, caches: 0, ...(error ? { error } : {}) });
}

/** Записва резултата така, че да преживее презареждането. */
function storeResult(result: RefreshResult): void {
  try {
    sessionStorage.setItem(RESULT_KEY, JSON.stringify(result));
  } catch {
    // Без sessionStorage просто няма да има обратна връзка — обновяването
    // само по себе си пак минава.
  }
}

export function useForceRefresh() {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const forceRefresh = useCallback(async () => {
    setIsRefreshing(true);

    let serviceWorkers = 0;
    let cacheCount = 0;

    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((r) => r.unregister()));
        serviceWorkers = registrations.length;
      }

      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
        cacheCount = cacheNames.length;
      }

      storeResult({ ok: true, serviceWorkers, caches: cacheCount });
    } catch (error) {
      // Провалът се ЗАПИСВА, не се преглъща: потребителят трябва да разбере,
      // че кешът вероятно още стои, вместо да реши, че е обновил.
      storeResult({
        ok: false,
        serviceWorkers,
        caches: cacheCount,
        error: error instanceof Error ? error.message : String(error),
      });
    }

    window.location.reload();
  }, []);

  return { forceRefresh, isRefreshing };
}
